import { useCallback, useEffect, useState } from 'react';
import ApiTokenBar from './ApiTokenBar';
import {
  fetchAuthSession,
  logoutSession,
  requireUserLogin,
  SESSION_KICKED_EVENT,
  shouldShowApiTokenBar,
  TOKEN_CHANGED_EVENT,
  getUserSessionToken,
} from './api/auth';
import { CreateUserPanel, TotpSetupPanel } from './AccountPanels';
import DashboardMt5 from './DashboardMt5';
import DashboardMt4 from './DashboardMt4';
import BackgroundLayout from './components/BackgroundLayout/BackgroundLayout';
import Menu from './components/Menu/Menu';
import LoginScreen from './LoginScreen';
import { ThemeProvider } from './theme/ThemeContext';
import { DateFilterProvider } from './context/DateFilterContext';
import { FilterBridgeProvider } from './context/FilterBridgeContext';
import { CardGridLayoutProvider } from './context/CardGridLayoutContext';
import './App.css';
import './LoginScreen.css';

function isAdminUser(user) {
  const v = user?.is_admin;
  return v === true || v === 1 || v === '1' || v === 'true';
}

function AuthenticatedApp({ user, onLogout, onUserUpdate }) {
  const [page, setPage] = useState('mt5');
  const [showCreate, setShowCreate] = useState(false);
  const [showTotp, setShowTotp] = useState(false);
  const isAdmin = isAdminUser(user);

  return (
    <CardGridLayoutProvider activePage={page}>
      <FilterBridgeProvider>
        <DateFilterProvider>
          <div className="app-shell">
            {shouldShowApiTokenBar() && <ApiTokenBar />}
            <Menu
              activePage={page}
              onPageChange={setPage}
              userEmail={user?.email}
              isAdmin={isAdmin}
              onLogout={onLogout}
              onOpenCreateUser={isAdmin ? () => setShowCreate(true) : undefined}
              onOpenTotp={() => setShowTotp(true)}
            />
            {showCreate && isAdmin ? (
              <CreateUserPanel
                isAdmin={isAdmin}
                onClose={() => setShowCreate(false)}
              />
            ) : null}
            {showTotp ? (
              <TotpSetupPanel
                totpEnabled={Boolean(user?.totp_enabled)}
                onUpdated={(patch) => onUserUpdate?.(patch)}
                onClose={() => setShowTotp(false)}
              />
            ) : null}
            <BackgroundLayout>
              <div className={`app-tab-panel${page === 'mt5' ? ' app-tab-panel--active' : ''}`}>
                <DashboardMt5 active={page === 'mt5'} />
              </div>
              <div className={`app-tab-panel${page === 'mt4' ? ' app-tab-panel--active' : ''}`}>
                <DashboardMt4 active={page === 'mt4'} />
              </div>
            </BackgroundLayout>
          </div>
        </DateFilterProvider>
      </FilterBridgeProvider>
    </CardGridLayoutProvider>
  );
}

function App() {
  const needLogin = requireUserLogin();
  const [authReady, setAuthReady] = useState(!needLogin);
  const [user, setUser] = useState(null);
  const [kickMessage, setKickMessage] = useState('');
  const [checking, setChecking] = useState(needLogin);

  const refreshSession = useCallback(async () => {
    if (!needLogin) {
      setAuthReady(true);
      setChecking(false);
      return;
    }
    if (!getUserSessionToken()) {
      setUser(null);
      setAuthReady(false);
      setChecking(false);
      return;
    }
    try {
      const sess = await fetchAuthSession();
      setUser(sess);
      setAuthReady(true);
      setKickMessage('');
    } catch (err) {
      setUser(null);
      setAuthReady(false);
      if (err?.status === 401) {
        setKickMessage(
          'Another person signed in to this account — please log in again.'
        );
      }
    } finally {
      setChecking(false);
    }
  }, [needLogin]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    const onToken = () => {
      if (needLogin) refreshSession();
    };
    const onKick = (ev) => {
      setKickMessage(
        ev?.detail?.message ||
          'Another person signed in to this account — please log in again.'
      );
      setUser(null);
      setAuthReady(false);
    };
    window.addEventListener(TOKEN_CHANGED_EVENT, onToken);
    window.addEventListener(SESSION_KICKED_EVENT, onKick);
    return () => {
      window.removeEventListener(TOKEN_CHANGED_EVENT, onToken);
      window.removeEventListener(SESSION_KICKED_EVENT, onKick);
    };
  }, [needLogin, refreshSession]);

  // Poll session so a parallel login kicks this browser.
  useEffect(() => {
    if (!needLogin || !authReady) return undefined;
    const id = setInterval(() => {
      refreshSession();
    }, 30_000);
    const onFocus = () => refreshSession();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [needLogin, authReady, refreshSession]);

  async function handleLogout() {
    await logoutSession();
    setUser(null);
    setAuthReady(false);
    setKickMessage('');
  }

  return (
    <ThemeProvider>
      {checking ? (
        <div className="login-screen">
          <div className="login-screen__card">
            <p className="login-screen__sub">Checking session…</p>
          </div>
        </div>
      ) : needLogin && !authReady ? (
        <LoginScreen
          kickMessage={kickMessage}
          onAuthenticated={async (res) => {
            const next = {
              ...(res.user || { email: res.email }),
              is_admin: isAdminUser(res.user) || isAdminUser(res),
            };
            setUser(next);
            setAuthReady(true);
            setKickMessage('');
            // Authoritative flags (is_admin) from /session
            try {
              const sess = await fetchAuthSession();
              setUser(sess);
            } catch {
              /* keep next from login response */
            }
          }}
        />
      ) : (
        <AuthenticatedApp
          user={user}
          onLogout={handleLogout}
          onUserUpdate={(patch) => setUser((u) => ({ ...(u || {}), ...patch }))}
        />
      )}
    </ThemeProvider>
  );
}

export default App;
